import { useState, useEffect } from "react";
import {
  createTestReview,
  listReviews,
  CreateTestReviewResponse,
  ReviewListItem,
} from "../lib/api";

export default function Admin() {
  const [apiKey, setApiKey] = useState(() => {
    return localStorage.getItem("dfx_admin_api_key") || "";
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState("");
  const [episodeId, setEpisodeId] = useState("test-episode-001");
  const [notes, setNotes] = useState("This is a test review session.");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CreateTestReviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviews, setReviews] = useState<ReviewListItem[]>([]);

  const handleLogin = async () => {
    setError(null);
    try {
      await listReviews(apiKey);
      localStorage.setItem("dfx_admin_api_key", apiKey);
      setIsAuthenticated(true);
      loadReviews();
    } catch {
      setError("Invalid API key");
    }
  };

  const loadReviews = async () => {
    try {
      const data = await listReviews(apiKey);
      setReviews(data);
    } catch {
      // Ignore errors
    }
  };

  useEffect(() => {
    if (apiKey) {
      listReviews(apiKey)
        .then(() => {
          setIsAuthenticated(true);
          loadReviews();
        })
        .catch(() => {
          // Not authenticated
        });
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await createTestReview(apiKey, {
        episode_id: episodeId,
        reviewer_emails: [email],
        notes,
      });
      setResult(response);
      setEmail("");
      loadReviews();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <h1 className="text-2xl font-bold text-white mb-6">Admin Login</h1>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              API Key
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter API key"
            />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button
            onClick={handleLogin}
            className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-white mb-6">Admin: Create Test Review</h1>

      <form onSubmit={handleSubmit} className="space-y-4 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Episode ID
          </label>
          <input
            type="text"
            value={episodeId}
            onChange={(e) => setEpisodeId(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Reviewer Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="reviewer@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || !email}
          className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-800 disabled:cursor-not-allowed text-white font-medium rounded-lg"
        >
          {isLoading ? "Sending..." : "Create Review & Send Magic Link"}
        </button>
      </form>

      {error && (
        <div className="p-4 bg-red-900/50 border border-red-700 rounded-lg mb-6">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {result && (
        <div className="p-4 bg-green-900/50 border border-green-700 rounded-lg mb-6">
          <h3 className="font-medium text-green-400 mb-2">Review Created</h3>
          <p className="text-sm text-gray-300 mb-1">
            <strong>Token:</strong> {result.review_token}
          </p>
          <p className="text-sm text-gray-300 mb-1">
            <strong>Emails sent:</strong> {result.emails_sent.join(", ") || "None"}
          </p>
          {result.emails_failed.length > 0 && (
            <p className="text-sm text-red-400">
              <strong>Failed:</strong> {result.emails_failed.join(", ")}
            </p>
          )}
          <p className="text-sm text-gray-400 mt-2">
            Check your inbox for the magic link.
          </p>
        </div>
      )}

      <h2 className="text-xl font-bold text-white mb-4">Recent Reviews</h2>
      {reviews.length === 0 ? (
        <p className="text-gray-400">No reviews yet.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <div
              key={review.review_token}
              className="p-4 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <div className="flex justify-between items-start mb-2">
                <span className="font-medium text-white">{review.episode_id}</span>
                <span
                  className={`text-xs px-2 py-1 rounded ${
                    review.status === "approved"
                      ? "bg-green-900 text-green-400"
                      : review.status === "rejected"
                      ? "bg-red-900 text-red-400"
                      : "bg-yellow-900 text-yellow-400"
                  }`}
                >
                  {review.status}
                </span>
              </div>
              <p className="text-sm text-gray-400">
                {review.reviewer_emails.join(", ")}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(review.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => {
          localStorage.removeItem("dfx_admin_api_key");
          setIsAuthenticated(false);
          setApiKey("");
        }}
        className="mt-8 text-sm text-gray-500 hover:text-gray-400"
      >
        Logout
      </button>
    </div>
  );
}
